import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import s from './textFieldsBlock.module.scss';
import { useSelector } from 'react-redux';
import { useStatusContext } from 'components/StatusProvider';
import { translateText } from 'utils/translateText';
import { ReactComponent as LoadingIcon } from "assets/icons/loading.svg";
import translateIcon from "assets/icons/translate.webp";

type TextFiledsBlockType = {
    title: string,
    values: {[key: string]: string},
    changeValue: (value: {[key: string]: string}) => void
    setLang?: (str:string) => void
    styles?: CSSProperties
    currentLang?: string
    activeLang?: any
    setActiveLang?: any
}

export const TextFieldsBlock: React.FC<TextFiledsBlockType> = ({title, values, changeValue, setLang, styles, activeLang, setActiveLang}) => {

    const langs = useSelector((store: any) => store.main.langs);
    const [isLoadingTranslate, setIsLoadingTranslate] = useState<boolean>(false);
    const { openStatus } = useStatusContext();
    const input = useRef<HTMLInputElement>(null)
    const MAX_TRANSLATE_LENGTH = 5000;

    // ### EFFECTS
    useEffect(()=>{
        if(input.current){
            const inputElement: HTMLInputElement = input.current.children[0] as HTMLInputElement
            inputElement?.focus()
        }
    },[activeLang]);

    if(!langs) return <></>

    const langs_view = langs?.map((el: any, index: number) => {
        const completed = values?.[el.key]?.length
        return(
            <div 
                key={index}
                className={`${s.lang_item} ${activeLang === el.key? s.active_lang_item : ''} ${completed? s.completed_lang_item : ''}`}
                onClick={()=>{
                    setActiveLang(el.key)
                    if(setLang) setLang(el.key)
                }}
            >
                {el.key}
                <span>{completed? <span>&#10004;</span> : <span>&#10008;</span>}</span>
            </div>
        )
    })

    function getCurrentValue(){
        let value
        
        for(let i=0; i<langs?.length; i++){
            if(values[langs?.[i].key]?.length){
                value = values[langs?.[i].key]
                break
            }
        }
        
        return value
    }

    const handleTranslate = async () => {
        try {
            setIsLoadingTranslate(true);
            const text = getCurrentValue();

            if (!text) {
                setIsLoadingTranslate(false);
                openStatus('error', "status.notPicture");
                return;
            }

            const chunks = [];
            let chunkStart = 0;

            while (chunkStart < text.length) {
                let chunkEnd = chunkStart + MAX_TRANSLATE_LENGTH;
                if (chunkEnd < text.length) {
                    const lastSpaceIndex = text.lastIndexOf(' ', chunkEnd);
                    if (lastSpaceIndex > chunkStart) {
                        chunkEnd = lastSpaceIndex;
                    }
                }
                chunks.push(text.slice(chunkStart, chunkEnd));
                chunkStart = chunkEnd + 1;
            }

            const languages = langs?.map((l: any) => l.key) || [];
            const results = await Promise.all(chunks.map(chunk => translateText(chunk, languages)));

            const mergedResult = results.reduce((acc, part) => {
                for (const key in part) {
                    acc[key] = (acc[key] || '') + part[key];
                }
                return acc;
            }, {} as Record<string, string>);
            
            Object.entries(mergedResult).forEach(obj => {
                changeValue({[obj[0]]: obj[1]})
            })
            
            setIsLoadingTranslate(false);
        } catch (error) {
            setIsLoadingTranslate(false);
            openStatus('error', "status.translationError");
            console.error("Error during translation:", error);
        }
    };

    return(
        <div className={s.wrapper}>
            <header>
                <p>{title}</p>
                <div className={s.langs_wrapper}>
                    {langs_view}
                </div>
                <div className={s.translator_wrapper}>
                    {isLoadingTranslate ? 
                        <LoadingIcon className={s.loadingIcon} /> :
                        <img
                            src={translateIcon}
                            alt={"translateIcon"}
                            className={s.translateIcon}
                            onClick={handleTranslate}
                        />
                    }
                </div>
            </header>
            <div className={s.input_wrapper}>
                <input
                    type="text"
                    ref={input}
                    style={{ width: "50%", maxWidth: 800, ...styles}}
                    value={values?.[activeLang]?.length? values[activeLang] : ''}
                    onChange={(e) => {
                        changeValue({[activeLang]: e.target.value})
                    }}
                />
            </div>
        </div>
    )
}